<template>
  <Form ref="formRef" v-bind="formConfigRef">
    <template #urlInforme__after>
      <i @click="copy()" class="pi pi-copy text-primary cursor-pointer"></i>
    </template>

    <template #addCategory__after>
      <Button
        label="Crear categoría"
        @click="emit('showCategoriesModal')"
        class="w-full"
      />
    </template>
  </Form>
</template>

<script setup>
import { ref, nextTick, watch } from "vue";
import { useToast } from "primevue/usetoast";
import Button from "primevue/button";
import { Form, useImmer } from "@sttedra_ui/framework";

import useFormConfig, {
  getCategories,
  categories,
} from "@/pages/_root/reports-dialog/form-one/report-form-one.view-config.js";
import {
  convertAliasToURL,
  convertUtcToLocalTime,
} from "@/composables/_commons/helper.js";

// #region Props, Emits
const emit = defineEmits(["showCategoriesModal", "esPublicoChanged"]);
// #endregion

// #region Params, Ref, Reactives, Computed
const toast = useToast();
const formRef = ref(null);
const formConfig = ref(null);
const { ref: formConfigRef, update: updateFormConfig } = useImmer(formConfig);
const register = ref(null);
const loaded = ref(false);
// #endregion

// #region Functions
async function loadForm(data) {
  await getCategories();

  if (Object.keys(data).length == 0) {
    formConfig.value = new useFormConfig(null, setURLInforme);
    loaded.value = true;
  } else {
    register.value = data;

    const object = Object.keys(data).map((key) => {
      return {
        name: key,
        value: data[key],
      };
    });

    formConfig.value = new useFormConfig(object, setURLInforme);

    nextTick(() => {
      if (formRef.value) {
        setURLInforme(data.alias);

        if (data.habilitadoDesde) {
          formRef.value.state$$.values.habilitadoDesde = convertUtcToLocalTime(
            data.habilitadoDesde
          );
        }

        if (data.publicoDesde) {
          formRef.value.state$$.values.publicoDesde = convertUtcToLocalTime(
            data.publicoDesde
          );
        }

        if (data.publicoHasta) {
          formRef.value.state$$.values.publicoHasta = convertUtcToLocalTime(
            data.publicoHasta
          );
        }
      }

      loaded.value = true;
    });
  }
}

async function validateForm() {
  return !(await formRef.value?.v$Ref.$validate());
}

function copy() {
  const urlInforme = formRef.value?.state$$.values.urlInforme;

  if (urlInforme) {
    navigator.clipboard.writeText(urlInforme).then(
      () => {
        toast.add({
          severity: "success",
          summary: "Copiado",
          detail: `Enlace copiado al portapapeles`,
          life: 3000,
        });
      },
      () => {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: `No se pudo copiar el enlace al portapapeles`,
          life: 3000,
        });
      }
    );
  }
}

function setURLInforme(alias = null) {
  const esPublico = formRef.value.state$$.values.esPublico;

  if (!alias) {
    alias = formRef.value.state$$.values.alias;
  }

  if (alias) {
    formRef.value.state$$.values.urlInforme = convertAliasToURL(
      alias,
      esPublico
    );
  }
}

// Load the category saved
function loadCategory(category, push = true) {
  if (push) {
    categories.push(category);
  }

  nextTick(() => {
    formRef.value.state$$.values.aliasCategoria = category.aliasCategoria;
  });
}

function getData() {
  const data = formRef.value?.state$$.values;

  data.tieneCabecera = data.tieneCabecera == null ? false : data.tieneCabecera;
  data.comentable = data.comentable == null ? false : data.comentable;
  data.habilitado = data.habilitado == null ? false : data.habilitado;
  data.esPublico = data.esPublico == null ? false : data.esPublico;

  return data;
}

function disableEsPublicoField(disabled) {
  updateFormConfig((config) => {
    if (config) {
      const items = config.items;

      items.forEach((item) => {
        if (item.field == "esPublico") {
          item.properties.disabled = disabled;
        }
      });

      return config;
    }
  });
}
// #endregion

// #region Effects, Hooks
watch(
  () => formRef.value?.state$$.values.esPublico,
  (value) => {
    if (loaded.value) {
      emit("esPublicoChanged", value);
    }

    updateFormConfig((config) => {
      const items = config.items;

      items.forEach((item) => {
        if (item.field == "publicoDesde" || item.field == "publicoHasta") {
          item.properties.disabled = value == null ? false : !value;
        }
      });

      return config;
    });
  }
);
// #endregion

// #region Provide, Expose
defineExpose({
  formRef,
  loadForm,
  loadCategory,
  validateForm,
  getData,
  disableEsPublicoField,
});
// #endregion
</script>
